/**
 * Delimited-text reader (.csv, .tsv) on PapaParse (MIT, the brief's
 * recommended parser). Delimited files carry no metadata, so labels
 * and formats are null and provenance is 'inferred' throughout.
 *
 * Typing rules, applied uniformly with the spreadsheet reader: a
 * column is 'number' when at least one cell is non-empty and every
 * non-empty cell parses as a number under a strict grammar (optional
 * sign, digits with optional decimal point, optional exponent — no
 * thousands separators, no currency, no percent signs; anything else
 * is text and stays text). Empty and whitespace-only cells are
 * missing (null). String cells are preserved verbatim, never trimmed.
 *
 * International formats: when no delimiter is given for a CSV file,
 * the delimiter is sniffed from the first lines (comma, semicolon,
 * or tab — whichever yields a consistent field count of two or
 * more; comma when ambiguous). In a semicolon-delimited file — the
 * standard European Excel export — cells additionally parse under
 * the European numeric grammar: a decimal comma ("12,5" is 12.5)
 * with optional thousands groups separated by '.', a plain space,
 * or a non-breaking space ("1.234,5" and "1 234,5" are both
 * 1234.5). The thousands reading takes precedence in that mode, so
 * "1.234" is 1234; a value that matches neither grammar, or only
 * the standard grammar, follows the standard grammar. Comma- and
 * tab-delimited files are never reinterpreted this way.
 */
import Papa from 'papaparse';
import { IoError, parseFailure } from './errors.js';
import { NO_USER_MISSING } from './types.js';
const NUMBER_RE = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;
/**
 * European numeric grammar for semicolon-delimited files: optional
 * sign; an integer part that is either plain digits or a leading
 * group of 1–3 digits followed by thousands groups of exactly
 * three digits separated by '.', ' ', or a non-breaking space; an
 * optional decimal-comma fraction; an optional exponent.
 */
const EURO_NUMBER_RE = /^[+-]?(?:\d{1,3}(?:[. \u00A0]\d{3})+|\d+)(?:,\d+)?([eE][+-]?\d+)?$/;
/** Convert a cell already validated against EURO_NUMBER_RE. */
function parseEuropeanNumber(trimmed) {
    const expAt = trimmed.search(/[eE]/);
    const mantissa = expAt === -1 ? trimmed : trimmed.slice(0, expAt);
    const exponent = expAt === -1 ? '' : trimmed.slice(expAt);
    // The grammar guarantees '.' and spaces in the mantissa are
    // thousands separators only, and at most one decimal comma.
    return Number(mantissa.replace(/[. \u00A0]/g, '').replace(',', '.') + exponent);
}
/**
 * Delimiter sniff: parse the first lines under each candidate and
 * keep the ones that split every sampled line into the same field
 * count of two or more. The widest consistent split wins; ties go
 * to the earliest candidate (comma first), and no consistent
 * candidate means comma — the historical default.
 */
function sniffDelimiter(text) {
    const lines = [];
    let start = 0;
    while (lines.length < 10 && start <= text.length) {
        const nl = text.indexOf('\n', start);
        const line = (nl === -1 ? text.slice(start) : text.slice(start, nl)).replace(/\r$/, '');
        if (line.trim() !== '')
            lines.push(line);
        if (nl === -1)
            break;
        start = nl + 1;
    }
    if (lines.length < 2)
        return ',';
    const sample = lines.join('\n');
    let best = ',';
    let bestWidth = 1;
    for (const candidate of [',', ';', '\t']) {
        const rows = Papa.parse(sample, {
            delimiter: candidate,
            skipEmptyLines: 'greedy',
        }).data;
        if (rows.length < 2)
            continue;
        const width = rows[0].length;
        if (width < 2 || width <= bestWidth)
            continue;
        if (!rows.every((r) => r.length === width))
            continue;
        best = candidate;
        bestWidth = width;
    }
    return best;
}
export function parseDelimitedText(text, options = {}) {
    const format = options.format ?? 'csv';
    const cleanText = text.replace(/^\uFEFF/, '');
    const delimiter = options.delimiter ??
        (format === 'tsv' ? '\t' : sniffDelimiter(cleanText));
    // Semicolon delimiters mark the European Excel convention: decimal
    // commas and '.'/space thousands separators (see the header note).
    const european = delimiter === ';';
    const issues = [];
    const parsed = Papa.parse(cleanText, {
        delimiter,
        skipEmptyLines: 'greedy',
    });
    for (const err of parsed.errors) {
        issues.push({
            code: 'IO_PARSE_WARNING',
            severity: 'warning',
            message: `The file has a formatting problem near row ${(err.row ?? 0) + 1}; the affected text was read as-is.`,
            detail: `${err.code}: ${err.message}`,
        });
    }
    const raw = parsed.data;
    if (raw.length === 0) {
        throw new IoError({
            code: 'IO_EMPTY_FILE',
            severity: 'error',
            message: 'This file is empty. Choose a file with a header row and at least one data row.',
        });
    }
    const header = raw[0];
    const body = raw.slice(1);
    if (body.length === 0) {
        throw new IoError({
            code: 'IO_NO_DATA_ROWS',
            severity: 'error',
            message: 'This file has a header row but no data rows.',
        });
    }
    const names = header.map((h, i) => {
        const name = (h ?? '').trim();
        if (name === '') {
            issues.push({
                code: 'IO_UNNAMED_COLUMN',
                severity: 'warning',
                message: `Column ${i + 1} has no header; it was named V${i + 1}. Rename it in the file if it should carry data.`,
                column: `V${i + 1}`,
            });
            return `V${i + 1}`;
        }
        return name;
    });
    const seen = new Map();
    for (const name of names) {
        const count = (seen.get(name) ?? 0) + 1;
        seen.set(name, count);
        if (count === 2) {
            issues.push({
                code: 'IO_DUPLICATE_COLUMN',
                severity: 'warning',
                message: `The column name "${name}" appears more than once. Column references use the first one; rename the duplicates in the file to use them.`,
                column: name,
            });
        }
    }
    // Cell conversion, column by column so typing is a column property.
    const width = names.length;
    const columnsCells = names.map(() => []);
    let ragged = 0;
    for (const row of body) {
        if (row.length !== width)
            ragged++;
        for (let i = 0; i < width; i++) {
            const rawCell = row[i];
            if (rawCell === undefined || rawCell.trim() === '') {
                columnsCells[i].push(null);
            }
            else {
                columnsCells[i].push(rawCell);
            }
        }
    }
    if (ragged > 0) {
        issues.push({
            code: 'IO_RAGGED_ROWS',
            severity: 'warning',
            message: `${ragged} row${ragged === 1 ? ' has' : 's have'} a different number of fields than the header; missing fields were read as empty.`,
        });
    }
    const isNumericCell = (trimmed) => NUMBER_RE.test(trimmed) || (european && EURO_NUMBER_RE.test(trimmed));
    const toNumber = (trimmed) => european && EURO_NUMBER_RE.test(trimmed)
        ? parseEuropeanNumber(trimmed)
        : Number(trimmed);
    const columns = names.map((name, i) => {
        const cells = columnsCells[i];
        const nonNull = cells.filter((c) => c !== null);
        const numeric = nonNull.length > 0 && nonNull.every((c) => isNumericCell(c.trim()));
        if (numeric) {
            for (let r = 0; r < cells.length; r++) {
                const c = cells[r];
                if (c !== null)
                    cells[r] = toNumber(c.trim());
            }
        }
        return {
            name,
            label: null,
            type: numeric ? 'number' : 'string',
            storageType: null,
            displayFormat: null,
            measure: 'unknown',
            valueLabels: [],
            userMissing: NO_USER_MISSING,
            provenance: 'inferred',
        };
    });
    const rows = body.map((_, r) => columns.map((_, i) => columnsCells[i][r]));
    const table = { format, fileLabel: null, columns, rows };
    return { table, issues };
}
export async function readDelimitedTable(input, options = {}) {
    const label = options.format === 'tsv' ? 'TSV' : 'CSV';
    try {
        const text = typeof input === 'string'
            ? input
            : new TextDecoder('utf-8').decode(input);
        return parseDelimitedText(text, options);
    }
    catch (cause) {
        if (cause instanceof IoError)
            throw cause;
        throw parseFailure(`${label} text`, cause);
    }
}
//# sourceMappingURL=delimited.js.map